import { useEffect, useMemo, useState } from "react";
import { ProductsAPI } from "../../api/products";
import { ReviewsAPI } from "../../api/reviews";
import { Product } from "../../types/products";

export default function ReviewCodesPage() {
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productQuery, setProductQuery] = useState("");
  const [productId, setProductId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    code: string;
    productName: string;
    createdAt?: string;
    expiresAt?: string;
  } | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingProducts(true);
      try {
        const response = await ProductsAPI.list({
          page: 1,
          limit: 500,
          includeInactive: true,
        });
        if (!mounted) return;
        const list = Array.isArray(response.items) ? response.items : [];
        setProducts(list);
        setProductId((prev) =>
          prev || (list.length > 0 && list[0]._id ? String(list[0]._id) : "")
        );
      } catch (e: any) {
        if (!mounted) return;
        setError(
          e?.response?.data?.message || e?.message || "Failed to load products."
        );
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const needle = productQuery.trim().toLowerCase();
    if (!needle) return products;
    return products.filter((product) =>
      [product.name, product.slug, product.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }, [productQuery, products]);

  const selectedProductName =
    products.find((product) => String(product._id || "") === productId)?.name ||
    "";

  const createCode = async () => {
    if (!productId) {
      setError("Select a product first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const data = await ReviewsAPI.createInStoreCode({
        productId,
        customerName: customerName.trim() || undefined,
        customerEmail: customerEmail.trim() || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
      });
      setResult({
        code: data.code,
        productName: selectedProductName || data.productId,
        createdAt: data.createdAt,
        expiresAt: data.expiresAt,
      });
      setCustomerName("");
      setCustomerEmail("");
      setExpiresAt("");
      setNotes("");
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to create in-store review code."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 920 }}>
      <h1 style={{ marginTop: 0, marginBottom: 8 }}>In-Store Review Codes</h1>
      <p style={{ marginTop: 0, color: "#4b5563" }}>
        Generate a one-time code for customers who purchased in-store. They can
        use this code on the product page to submit a verified review.
      </p>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 16,
          display: "grid",
          gap: 10,
          background: "#fff",
        }}
      >
        <label style={{ fontSize: 13, color: "#374151" }}>
          Find product
          <input
            value={productQuery}
            onChange={(e) => setProductQuery(e.target.value)}
            placeholder="Search by name, slug, category"
            style={{
              marginTop: 6,
              width: "100%",
              padding: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
            }}
          />
        </label>

        <label style={{ fontSize: 13, color: "#374151" }}>
          Product
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            style={{
              marginTop: 6,
              width: "100%",
              padding: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
            }}
          >
            {loadingProducts ? (
              <option>Loading products...</option>
            ) : (
              filteredProducts.map((product) => (
                <option key={String(product._id)} value={String(product._id)}>
                  {product.name} ({product.category})
                </option>
              ))
            )}
          </select>
        </label>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
          <label style={{ fontSize: 13, color: "#374151" }}>
            Customer name (optional)
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              style={{
                marginTop: 6,
                width: "100%",
                padding: 8,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            />
          </label>
          <label style={{ fontSize: 13, color: "#374151" }}>
            Customer email (optional)
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              style={{
                marginTop: 6,
                width: "100%",
                padding: 8,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
              }}
            />
          </label>
        </div>

        <label style={{ fontSize: 13, color: "#374151" }}>
          Expires at (optional)
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            style={{
              marginTop: 6,
              width: "100%",
              padding: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
            }}
          />
        </label>

        <label style={{ fontSize: 13, color: "#374151" }}>
          Notes (optional)
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{
              marginTop: 6,
              width: "100%",
              padding: 8,
              border: "1px solid #e5e7eb",
              borderRadius: 8,
            }}
          />
        </label>

        {error ? (
          <div
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              background: "#fef2f2",
              color: "#991b1b",
              fontSize: 13,
              border: "1px solid #fecaca",
            }}
          >
            {error}
          </div>
        ) : null}

        <button
          onClick={createCode}
          disabled={submitting || !productId}
          style={{
            width: "fit-content",
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: submitting ? "#6b7280" : "#111827",
            color: "white",
            fontWeight: 600,
            cursor: submitting ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Generating..." : "Generate Review Code"}
        </button>
      </div>

      {result ? (
        <div
          style={{
            marginTop: 14,
            border: "1px solid #bbf7d0",
            background: "#ecfdf5",
            borderRadius: 12,
            padding: 14,
          }}
        >
          <div style={{ fontWeight: 700, color: "#065f46", marginBottom: 6 }}>
            Code generated
          </div>
          <div style={{ display: "grid", gap: 4, color: "#065f46" }}>
            <div>
              <strong>Code:</strong> {result.code}
            </div>
            <div>
              <strong>Product:</strong> {result.productName}
            </div>
            {result.expiresAt ? (
              <div>
                <strong>Expires:</strong>{" "}
                {new Date(result.expiresAt).toLocaleString()}
              </div>
            ) : null}
            {result.createdAt ? (
              <div>
                <strong>Created:</strong>{" "}
                {new Date(result.createdAt).toLocaleString()}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
